import * as angular from 'angular';
import * as moment from 'moment';
import { Mutex } from 'async-mutex';

import * as constants from '../../common/constants';
import * as events from '../../common/events';

import { IKeycloakService, ILoggerService, ILoginData, ISignalrService } from './';
import { IUserService, ICurrentApplicationService, IApplicationService } from '../data';
import { IAuthentication, ISessionStorageService, IWorkspaceService, IAuthenticationHolderService } from '../general';
import { IRootScope } from '../../IRootScope';
import { Workspace } from '../../models/Workspace';
import { IRequestService } from '../../services';
import { StoredToken } from './StoredToken';
import { TokenResponse } from './TokenResponse';
import computeExpiryTime from './computeExpiryTime';
import { refreshToken } from './helpers/refreshToken';
import { IMsalAuthenticationService } from '@services/msalAuthentication.provider';

const emptyAuthentication: IAuthentication = {
    datePickerFormat: null,
    isAuth: false,
    isConnected: false,
    isSuperAdmin: false,
    roles: [],
    userGuid: '',
    userId: '',
    userName: '',
    useRefreshTokens: false
};

export interface ILoginResult {
    url: string;
    params: IAuthentication;
}

export interface IAuthenticationService {
    authentication: Readonly<IAuthentication>;
    // store migrated
    updateUserData(values: { datePickerFormat?: string }): void;
    connect(userName: string, workspace: any, applicationGuid: string, showToast: boolean): angular.IPromise<any>;
    connectByApplicationName(applicationName: string, applicationGuid: string): angular.IPromise<any>;
    connectByWorkspace(userName: string, workspace: Workspace): angular.IPromise<any>;
    disconnect(): void;
    login(loginData: ILoginData, logoutOnFail?: boolean): angular.IPromise<ILoginResult>;
    logout(): void;
    getAuthData(): angular.IPromise<IAuthentication>;
    refreshToken(): angular.IPromise<StoredToken>;
}

const mutex = new Mutex();

export class AuthenticationService implements IAuthenticationService {
    static serviceId = 'authenticationService';

    auths: any[];
    authentication: IAuthentication;

    static $inject = [
        '$http',
        '$q',
        '$rootScope',
        'requestService',
        'userService',
        'sessionStorageService',
        'workspaceService',
        'applicationService',
        'currentApplicationService',
        'authenticationHolderService',
        'loggerService',
        'clientId',
        'signalrService',
        'msalAuthenticationService',
        'keycloakService'
    ];
    constructor(
        private _$http: angular.IHttpService,
        private _$q: angular.IQService,
        private _$rootScope: IRootScope,
        private _requestService: IRequestService,
        private _userService: IUserService,
        private _sessionStorageService: ISessionStorageService,
        private _workspaceService: IWorkspaceService,
        private _applicationService: IApplicationService,
        private _currentApplicationService: ICurrentApplicationService,
        private _authHolderService: IAuthenticationHolderService,
        private _loggerService: ILoggerService,
        private _clientId: string,
        private _signalrService: ISignalrService,
        private _msalAuthenticationService: IMsalAuthenticationService,
        private _keycloakService: IKeycloakService
    ) {
        this.authentication = this._authHolderService.authentication;
    }

    login(loginData: ILoginData, logoutOnFail: boolean = true): angular.IPromise<ILoginResult> {
        const user = encodeURIComponent(loginData.userName);
        const pass = encodeURIComponent(loginData.password);
        const data = `grant_type=password&username=${user}&password=${pass}&client_id=${this._clientId}`;
        const url = this._requestService.getApiUrl() + 'token';
        const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
        const sessionId = loginData.session ? encodeURIComponent(loginData.session) : undefined;
        if (sessionId) {
            headers['Wt-Session-Id'] = sessionId;
        }

        return this._$http
            .post<TokenResponse>(url, data, { headers: headers })
            .then((responseLogin) => {
                return this.postLogin(sessionId, responseLogin);
            })
            .catch((err) => {
                if (logoutOnFail) {
                    this.logout();
                }
                throw err;
            });
    }

    async refreshToken() {
        await mutex.waitForUnlock();
        if (!mutex.isLocked()) {
            const release = await mutex.acquire();
            try {
                if (!this._keycloakService.isKeycloakConfigured()) {
                    let token = this._sessionStorageService.getTypedFrom<any>(constants.authorizationDataKey);
                    const { sessionId } = token;
                    if (token?.refresh_token) {
                        const newToken = await refreshToken(token, { apiUrl: this._requestService.getApiUrl() });
                        token = this.setSessionStorageToken(sessionId, { data: newToken });
                    }
                    return token;
                } else {
                    return this._keycloakService.refreshToken();
                }
            } catch (e) {
                console.error(e);
                throw e;
            } finally {
                release();
            }
        } else {
            await mutex.waitForUnlock();
            return this._sessionStorageService.getTypedFrom<any>(constants.authorizationDataKey);
        }
    }

    private getMe(mustChangePassword: boolean) {
        return this._userService.getMe().then((payload) => {
            this.authentication.datePickerFormat = payload.user.datePickerFormat;
            this.authentication.isAuth = true;
            this.authentication.isSuperAdmin = payload.user.role?.toLowerCase() === constants.superAdminRole;
            this.authentication.userGuid = payload.user.userGuid;
            this.authentication.userId = payload.user.userId;
            this.authentication.userName = payload.user.userName;
            // Retriev info after request, to make sure we have the last token stored in case of refresh
            const storedInfo = this._sessionStorageService.getTypedFrom<StoredToken>(constants.authorizationDataKey);
            //update session storage to store users information
            const json: any = {};
            json.datePickerFormat = this.authentication.datePickerFormat;
            json.isSuperAdmin = this.authentication.isSuperAdmin;
            json.userGuid = this.authentication.userGuid;
            json.userId = this.authentication.userId;
            json.userName = this.authentication.userName;
            const stringify = JSON.stringify(Object.assign({}, storedInfo, json));
            this._sessionStorageService.setTo(constants.authorizationDataKey, stringify);

            const result: ILoginResult = mustChangePassword ? { url: 'passwordChange', params: this.authentication } : null;
            return result;
        });
    }

    private postLogin(sessionId: string, responseLogin: angular.IHttpResponse<TokenResponse>) {
        this.setSessionStorageToken(sessionId, responseLogin);
        const mustChangePassword = responseLogin.headers('Wt-Authentication') === 'MustChange';
        return this.getMe(mustChangePassword);
    }

    private setSessionStorageToken(sessionId: string, responseLogin: { data: TokenResponse }) {
        if (sessionId) {
            // clear IE previous auth if any
            this.clearIEAuthCache();
        }
        const { data } = responseLogin;
        data['.expires'] = computeExpiryTime(data);

        const expiration = new Date(data['.expires']);
        this.authentication.expirationDate = expiration;
        this.authentication.isAuth = true;
        const existingToken = this._sessionStorageService.getFrom(constants.authorizationDataKey);
        const json: StoredToken = Object.assign({}, existingToken ? JSON.parse(existingToken) : {}, {
            sessionId,
            token: data.access_token,
            expirationDate: expiration.toISOString(),
            expiryDate: expiration.toISOString(),
            refreshToken: data.refresh_token,
            refresh_token: data.refresh_token
        });
        const stringify = JSON.stringify(json);
        this._sessionStorageService.setTo(constants.authorizationDataKey, stringify);
        return json;
    }

    logout(): void {
        Object.assign(this.authentication, emptyAuthentication);
        const authData = this._sessionStorageService.getTypedFrom<StoredToken>(constants.authorizationDataKey);
        if (authData) {
            this.logoutFrontend();
            this._requestService.postAtV1('account/logout', authData);
        }
    }

    connectByWorkspace(userName, workspace: Workspace): angular.IPromise<any> {
        const defered = this._$q.defer();
        try {
            this._requestService.cleanAllCache();

            this._workspaceService.setWorkspace(workspace);
            this._applicationService
                .getByName(workspace.application)
                .then((application) => {
                    return this._$q.all([
                        this._currentApplicationService.setApplication(application),
                        this._applicationService.getRolesForApplication(application.guid, userName)
                    ]);
                })
                .then((data) => {
                    this.onConnection(data[1], workspace, userName, true);
                    return defered.resolve();
                });
        } catch (e) {
            this._loggerService.logError(e);
            defered.reject(e);
        }
        return defered.promise;
    }

    connectByApplicationName(applicationName: string, applicationGuid: string): angular.IPromise<any> {
        const workspace = {
            application: applicationName,
            applicationGuid,
            environment: 'Development',
            model: 'Model'
        };
        return this.connect(this.authentication.userName, workspace, applicationGuid, true);
    }

    connect(userName, workspace: Workspace, applicationGuid: string, showToast: boolean): angular.IPromise<void> {
        const defered = this._$q.defer<void>();
        try {
            this._requestService.cleanAllCache();

            this._workspaceService.setWorkspace(workspace);
            let application;
            this._applicationService
                .getByGuid(applicationGuid)
                .then((data) => {
                    application = data.application;
                    return this._currentApplicationService.setApplication(application);
                })
                .then(() => {
                    return this._applicationService.getRolesForApplication(application.guid, userName);
                })
                .then((data) => {
                    this.onConnection(data, workspace, userName, showToast);
                    return defered.resolve();
                })
                .catch((err) => defered.reject(err));
        } catch (e) {
            this._loggerService.logError(e);
            defered.reject(e);
        }
        return defered.promise;
    }

    disconnect() {
        this._currentApplicationService.resetApplication();
        this.authentication.roles = [];
        this.authentication.isConnected = false;
        this._requestService.cleanAllCache();
        this._workspaceService.clearWorkspace();
        this._sessionStorageService.clearApplicationData();
        this._$rootScope.$broadcast(events.applicationDisconnected);
    }

    private fillAuthData(authData, workspace): void {
        if (authData) {
            this.authentication.datePickerFormat = authData.datePickerFormat;
            this.authentication.isAuth = true;
            this.authentication.isConnected = !!workspace;
            this.authentication.isSuperAdmin = authData.isSuperAdmin;
            this.authentication.roles = authData.roles;
            this.authentication.userGuid = authData.userGuid;
            this.authentication.userId = authData.userId;
            this.authentication.userName = authData.userName;
            this.authentication.expirationDate = moment(authData.expirationDate).toDate();
        }
    }

    getAuthData() {
        let authData = JSON.parse(this._sessionStorageService.getFrom(constants.authorizationDataKey));
        const workspace = this._workspaceService.getWorkspace();
        this.fillAuthData(authData, workspace);
        if (authData?.token || authData?.refreshToken) {
            this.authentication.isAuth = true;
            return this.getMe(false)
                .then((_data) => {
                    //Above request might have changed stored token
                    authData = JSON.parse(this._sessionStorageService.getFrom(constants.authorizationDataKey));
                    this.fillAuthData(authData, workspace);
                    if (this.authentication.isConnected) {
                        return this._applicationService
                            .getByName(workspace.application)
                            .then((application) => {
                                return this._applicationService.getRolesForApplication(application.guid, this.authentication.userName);
                            })
                            .catch((err) => {
                                console.error(err);
                                throw err;
                            });
                    } else {
                        return this._$q.when([]);
                    }
                })
                .then((data) => {
                    this.authentication.roles = data;
                    //Above requests might have changed stored token
                    authData = JSON.parse(this._sessionStorageService.getFrom(constants.authorizationDataKey));
                    this.fillAuthData(authData, workspace);
                    Object.assign(authData, { roles: data });
                    this._sessionStorageService.setTo(constants.authorizationDataKey, JSON.stringify(authData));
                    return this.authentication;
                })
                .catch((err) => {
                    this.logout();
                    throw err;
                });
        } else {
            return this._$q.when(this.authentication);
        }
    }

    updateUserData(values: { datePickerFormat?: string }) {
        const authData = JSON.parse(this._sessionStorageService.getFrom(constants.authorizationDataKey));
        Object.keys(values).forEach((key) => {
            authData[key] = values[key];
            this.authentication[key] = values[key];
        });
        const stringify = JSON.stringify(authData);
        this._sessionStorageService.setTo(constants.authorizationDataKey, stringify);
    }

    private onConnection(data: string[], workspace: Workspace, userName: string, showToast: boolean) {
        this.authentication.roles = data;

        const authData = JSON.parse(this._sessionStorageService.getFrom(constants.authorizationDataKey));
        authData.roles = data;
        this._sessionStorageService.setTo(constants.authorizationDataKey, JSON.stringify(authData));

        this._$rootScope.application = workspace.application;

        this._$rootScope.$broadcast(events.applicationConnected, workspace);

        if (this.authentication.isAuth) {
            this.authentication.isConnected = true;
        }
        this._loggerService.logSuccess(`You're now connected ${userName}`, showToast);
        const result = this._$q.defer<void>();
        this._signalrService
            .connect()
            .then(() => result.resolve())
            .catch(() => result.reject());
    }

    private clearIEAuthCache(): void {
        const command = 'ClearAuthenticationCache';
        if (document.queryCommandSupported(command)) {
            document.execCommand(command, false);
        }
    }

    private logoutFrontend(): void {
        this.disconnect();
        this._sessionStorageService.clearApplicationData();
        this._sessionStorageService.remove(constants.authorizationDataKey);
        this._requestService.cleanAllCache();
        this._$rootScope.$broadcast(events.applicationLogout);
        try {
            this._msalAuthenticationService.clearCache();
            if (this._keycloakService.isKeycloakConfigured()) {
                this._keycloakService.logout();
            }
        } catch (e) {
            console.error(e);
        }
    }
}
